import React, { useState } from 'react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      return String(first);
    }
    if (typeof data.detail === 'string') return data.detail;
  }
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  if (data.message) return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
  return `Request failed with status ${status}`;
};

const PaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amount = parseFloat(formData.amount);
    const balanceDue = parseFloat(invoice.balance_due);
    if (!formData.amount || isNaN(amount)) {
      setError('Please enter a valid payment amount');
      return;
    }
    if (amount <= 0) {
      setError('Payment amount must be greater than 0');
      return;
    }
    if (amount > balanceDue) {
      setError(`Payment amount cannot exceed balance due (Rs. ${balanceDue.toLocaleString()})`);
      return;
    }
    setLoading(true);
    try {
      await api.post(`/invoices/${invoice.id}/record-payment`, {
        amount: amount,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        reference: formData.reference || null,
        notes: formData.notes || null
      });
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Payment recording error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title="Record Payment"
      onClose={onClose}
      maxWidth="max-w-md"
      dataTestId="payment-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="payment-form"
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="record-payment-button"
          >
            {loading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      }
    >
      <form id="payment-form" onSubmit={handleSubmit}>
        {error && (
          <div className={errorBoxClass} data-testid="payment-error">
            {error}
          </div>
        )}

        <div className="mb-4 p-4 bg-white rounded-lg border border-[#d1d5db]">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-medium text-[#1a1a2e]">Invoice:</span>
            <span className="text-sm text-[#6b7280]">{invoice.invoice_number}</span>
          </div>
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-medium text-[#1a1a2e]">Customer:</span>
            <span className="text-sm text-[#6b7280]">{invoice.customer?.name}</span>
          </div>
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-medium text-[#1a1a2e]">Total:</span>
            <span className="text-sm text-[#6b7280]">Rs. {parseFloat(invoice.total_amount).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-medium text-[#1a1a2e]">Paid:</span>
            <span className="text-sm text-[#6b7280]">Rs. {parseFloat(invoice.amount_paid).toLocaleString()}</span>
          </div>
          <div className="flex justify-between items-center border-t border-[#e5e7eb] pt-1.5 mt-1.5">
            <span className="text-sm font-semibold text-[#1a1a2e]">Balance Due:</span>
            <span className="text-sm font-semibold text-[#EF4444]">Rs. {parseFloat(invoice.balance_due).toLocaleString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Payment Amount (PKR) {requiredMark}</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={parseFloat(invoice.balance_due)}
              className={inputClass}
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              data-testid="amount-input"
              placeholder="Enter payment amount"
            />
          </div>
          <div>
            <label className={labelClass}>Payment Method {requiredMark}</label>
            <select
              className={selectClass}
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              required
              data-testid="payment-method-select"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="online">Online Payment</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Payment Date</label>
            <input
              type="date"
              className={inputClass}
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              data-testid="payment-date-input"
            />
          </div>
          <div>
            <label className={labelClass}>Reference</label>
            <input
              type="text"
              className={inputClass}
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              data-testid="reference-input"
              placeholder="Cheque #, Transaction ID"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className={labelClass}>Notes</label>
          <textarea
            className={textareaClass}
            rows="2"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            data-testid="notes-input"
            placeholder="Additional notes"
          />
        </div>
      </form>
    </ModalShell>
  );
};

export default PaymentModal;
